import _ from 'lodash';
import { AppConstants } from 'constants/index';
import i18n from 'services/i18n';
import { booleanTypesWithAll } from 'constants/mapping';

/**
 * Convert an array to a tree-structured array.
 * @param   {array}     array     The Array need to Converted.
 * @param   {string}    id        The alias of the unique ID of the object in the array.
 * @param   {string}    parentId       The alias of the parent ID of the object in the array.
 * @param   {string}    children  The alias of children of the object in the array.
 * @return  {array}    Return a tree-structured array.
 */
export function arrayToTree<T, IdKey extends keyof T, ParentKey extends keyof T>(array: T[], id: IdKey, parentId: ParentKey): Array<T & { children?: T[] }> {
  type TreeNode = T & { children?: T[] };

  const result: TreeNode[] = [];
  const hash: Record<string | number, TreeNode> = {};

  array.forEach((item, index) => {
    const key = item[id] as unknown as string | number;

    hash[key] = array[index] as TreeNode;
  });

  // Second pass
  array.forEach((item) => {
    const node = item as TreeNode;
    const parentKey = node[parentId] as unknown as string | number | undefined;

    if (parentKey !== undefined && hash[parentKey]) {
      const parent = hash[parentKey];

      parent.children ??= [];
      parent.children.push(node);
    } else {
      result.push(node);
    }
  });
  return result;
}

/**
 * Convert an input value to number.
 * @param {Object} e The event of input.
 * @param {Number} precision symbols count after the point
 * @param {Boolean} isNegative The type of input
 * @returns {null|String} Return a converted value.
 */
export const onlyNumbersFormatter = ({ target: { value } }: { target: { value: string } }, precision: number = 0, isNegative: boolean = false): string => {
  if (!value) {
    return '';
  }

  let onlyDigits: string | RegExpMatchArray | null = value.replace(AppConstants.regex.notNDotUnderscore, '');

  if (precision) {
    onlyDigits = onlyDigits?.match(isNegative ? AppConstants.regex.negativeNumber : AppConstants.regex.doublePositiveNumber);
    const dotPosition = value.indexOf('.');

    if (dotPosition !== -1 && onlyDigits && onlyDigits[0]) {
      return onlyDigits[0].slice(0, dotPosition + precision + 1);
    }
  } else {
    onlyDigits = onlyDigits?.match(isNegative ? AppConstants.regex.negIntNumber : AppConstants.regex.intNumber);
  }

  return (onlyDigits && onlyDigits[0]) || '';
};

/**
 * Download file.
 * @param content Stream or list: string or array of arrays (for CSV).
 * @param fileName Generated filename.
 * @param extension File extension (default: 'csv').
 */
export const downloadFile = (content: string | Array<Array<string | number>> | Blob, fileName: string, extension?: string | false): void => {
  const ext = extension || 'csv';
  const link = document.createElement('a');
  if (link.download !== undefined) {
    let stream: string = '';
    if (ext === 'csv' && Array.isArray(content)) {
      content.forEach((row: Array<string | number>) => {
        stream += row.join(',');
        stream += '\n';
      });
    } else {
      stream = content as string;
    }

    const blob = new Blob([stream], { type: 'octet/stream' });
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.${ext}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

/**
 * Get file name and extension
 * @param {string} filename with extension.
 * @param {boolean} isExtensionLowerCased.
 * @returns {[string, string]} Return file name, file extension.
 */
export const getFileNameAndExtension = (filename: string, isExtensionLowerCased: boolean = true): [string, string] => {
  const index = filename.lastIndexOf('.');
  return [filename.substring(0, index), isExtensionLowerCased ? filename.substring(index + 1).toLowerCase() : filename.substring(index + 1)];
};

/**
 *Copy code to clipBoard
 * @param value
 */
export const copyCodeToClipBoard = (value: string) => navigator.clipboard.writeText(value);

/**
 * Get file size
 * @param bytes
 * @param decimals
 */
export const getFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return `0 ${i18n.t('memorySize.bytes')}`;
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [i18n.t('memorySize.bytes'), i18n.t('memorySize.kb'), i18n.t('memorySize.mb'), i18n.t('memorySize.gb')];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

/**
 * @returns {string} string without HTML Tags, Spaces, New Lines
 * @param str
 */
export const isEmptyHtml = (str: string): string => str.replace(AppConstants.regex.emptyHtml, '$1').trim();
/**
 * @param String string
 * @returns {string} remove HTML tags
 */
export const stripTags = (str: string): string => str.replace(AppConstants.regex.htmlTag, '');
/**
 *
 * @param {*} String value
 * @returns {true|false } Boolean
 * @param value
 */
export const isTrue = (value: string): boolean => value === 'true';

/**
 * To check if is JSON
 * @param {string} text.
 * @returns {boolean}
 */
export const isJSON = (text: string): boolean => {
  if (!_.isString(text)) {
    return false;
  }
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * Generate Map from object
 * @param order
 * @param obj
 * @returns {*}
 */
export const generateMapByOrder = (order: (string | number)[], obj: Record<string, string | number> = {}): Map<string | number, string> => order.reduce((result, current) => {
  result.set(current, obj[current].toString());
  return result;
}, new Map<string | number, string>());

/**
 * @returns {number} random number
 */
export const getRandomNumber = (): number => {
  const crypto = window.crypto || (window as unknown as { msCrypto: Crypto }).msCrypto;
  const array = new Uint32Array(1);
  return crypto.getRandomValues(array)[0];
};

/**
 * Converts exponential notation to a human's readable string
 * @param {number|string|Array} num - number or array of its parts
 * @return {string}
 */
export const fromExponential = (num: number | string | Array<string>): string => {
  const getExponentialParts = (numb: number | string | Array<string>) => (Array.isArray(numb) ? numb : String(numb).split(/[eE]/));

  const isExponential = (numb: number | string | Array<string>) => {
    const eParts = getExponentialParts(numb);
    return !Number.isNaN(Number(eParts[1]));
  };

  const eParts = getExponentialParts(num);
  if (!isExponential(eParts)) {
    return eParts[0];
  }

  const sign = eParts[0][0] === '-' ? '-' : '';
  const digits = eParts[0].replace(/^-/, '');
  const digitsParts = digits.split('.');
  const wholeDigits = digitsParts[0];
  const fractionDigits = digitsParts[1] || '';
  let e = Number(eParts[1]);

  if (e === 0) {
    return `${sign + wholeDigits}.${fractionDigits}`;
  }
  if (e < 0) {
    const countWholeAfterTransform = wholeDigits.length + e;
    if (countWholeAfterTransform > 0) {
      const wholeDigitsAfterTransform = wholeDigits.substr(0, countWholeAfterTransform);
      const wholeDigitsTransformedToFraction = wholeDigits.substr(countWholeAfterTransform);
      return `${sign + wholeDigitsAfterTransform}.${wholeDigitsTransformedToFraction}${fractionDigits}`;
    }
    let zeros = '0.';
    e = countWholeAfterTransform;
    while (e) {
      zeros += '0';
      e += 1;
    }
    return sign + zeros + wholeDigits + fractionDigits;
  }
  const countFractionAfterTransform = fractionDigits.length - e;
  if (countFractionAfterTransform > 0) {
    const fractionDigitsAfterTransform = fractionDigits.substr(e);
    const fractionDigitsTransformedToWhole = fractionDigits.substr(0, e);
    return `${sign + wholeDigits + fractionDigitsTransformedToWhole}.${fractionDigitsAfterTransform}`;
  }
  let zerosCount = -countFractionAfterTransform;
  let zeros = '';
  while (zerosCount) {
    zeros += '0';
    zerosCount -= 1;
  }
  return sign + wholeDigits + fractionDigits + zeros;
};

/**
 * @param html
 * @returns {string}
 */

export const decodeHtml = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

/**
 * Hour representation
 * @param hour
 * @returns {string}
 */

export const getHour = (hour: number): string => (hour < 10 ? `0${hour}` : `${hour}`);
/**
 * Object deep comparison with extra identity check
 * @param a {object}
 * @param b {object}
 * @returns {boolean}
 */

export const isDeepEqual = (a: object, b: object): boolean => _.isEqual(_.pickBy(a, _.identity), _.pickBy(b, _.identity));
/**
 * Safely converts a value to a boolean
 * @param val {string}
 * @param fallback {boolean}
 * @returns {boolean|undefined}
 */

export const strToBool = (val: string | undefined, fallback?: boolean): boolean | undefined => (!_.isUndefined(val) ? isTrue(val) : fallback);

export const getRangeSet = (from: number, to: number, max: number): Set<number> => {
  if (!from) return new Set();

  const end = to || from;

  return new Set(from <= end ? _.range(from, end + 1) : [..._.range(from, max + 1), ..._.range(1, end + 1)]);
};

/**
 * Converts a decimal odds value to American format string.
 * e.g. 3.0 → "+200", 1.5 → "-200"
 */
export const decimalToAmerican = (decimal?: number | string): string => {
  if (!decimal) return '';
  const num = parseFloat(String(decimal));
  if (!num || num <= 1) return '';
  const value = num < 2 ? Math.round(-100 / (num - 1)) : Math.round((num - 1) * 100);
  return num < 2 ? `${value}` : `+${value}`;
};

type BoolValueResult<T extends boolean> =
    T extends true ? boolean | undefined : number | undefined;
/**
 * Get boolean value from string with "All" option
 */
export const getBoolValueWithAll = <T extends boolean = false>(val = String(booleanTypesWithAll.No), toBool = false as T): BoolValueResult<T> => {
  if (+val === booleanTypesWithAll.All) {
    return undefined as BoolValueResult<T>;
  }

  return (toBool ? !!+val : +val) as BoolValueResult<T>;
};
